import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool
} from 'amazon-cognito-identity-js'
import env from '../config/env'
import { isLocalE2E } from '../config/e2e'

const userPool = new CognitoUserPool({
  UserPoolId: env.cognitoUserPoolId,
  ClientId: env.cognitoWebClientId
})

let challengedUser = null

const principalFromSession = (user, session) => {
  const idPayload = session.getIdToken().decodePayload()
  const accessPayload = session.getAccessToken().decodePayload()

  const groups = [
    ...(idPayload['cognito:groups'] || []),
    ...(accessPayload['cognito:groups'] || [])
  ]

  return {
    username: user.getUsername(),
    sub: idPayload.sub,
    groups: [...new Set(groups)],
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken()
  }
}

const getSessionForUser = (user, { forceRefresh = false } = {}) => new Promise((resolve, reject) => {
  user.getSession((error, session) => {
    if (error) {
      reject(error)
      return
    }

    if (!session || !session.isValid()) {
      resolve(null)
      return
    }

    if (!forceRefresh) {
      resolve(principalFromSession(user, session))
      return
    }

    user.refreshSession(session.getRefreshToken(), (refreshError, refreshedSession) => {
      if (refreshError) {
        reject(refreshError)
        return
      }
      resolve(principalFromSession(user, refreshedSession))
    })
  })
})

const getCurrentPrincipal = async ({ forceRefresh = false } = {}) => {
  if (isLocalE2E()) {
    return {
      username: 'e2e-user',
      sub: 'e2e-sub',
      groups: ['family'],
      idToken: 'e2e-test-id-token',
      accessToken: 'e2e-test-access-token'
    }
  }

  const user = userPool.getCurrentUser()
  if (!user) {
    return null
  }

  try {
    return await getSessionForUser(user, { forceRefresh })
  } catch (_) {
    return null
  }
}

const signIn = (username, password) => new Promise((resolve, reject) => {
  const user = new CognitoUser({
    Username: username,
    Pool: userPool
  })

  const authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: password
  })

  user.authenticateUser(authenticationDetails, {
    onSuccess (session) {
      challengedUser = null
      resolve({
        type: 'authenticated',
        principal: principalFromSession(user, session)
      })
    },
    onFailure (error) {
      challengedUser = null
      reject(error)
    },
    newPasswordRequired (_userAttributes, requiredAttributes) {
      challengedUser = user
      resolve({
        type: 'newPasswordRequired',
        requiredAttributes: requiredAttributes || []
      })
    }
  })
})

const completeNewPassword = newPassword => new Promise((resolve, reject) => {
  if (!challengedUser) {
    reject(new Error('No new-password challenge is active.'))
    return
  }

  challengedUser.completeNewPasswordChallenge(newPassword, {}, {
    onSuccess (session) {
      const user = challengedUser
      challengedUser = null
      resolve(principalFromSession(user, session))
    },
    onFailure (error) {
      reject(error)
    }
  })
})

const signUp = (username, email, password) => new Promise((resolve, reject) => {
  const attributes = [
    new CognitoUserAttribute({
      Name: 'email',
      Value: email
    })
  ]

  userPool.signUp(username, password, attributes, null, (error, result) => {
    if (error) {
      reject(error)
      return
    }

    resolve({
      username: result.user.getUsername(),
      userConfirmed: result.userConfirmed
    })
  })
})

const confirmSignUp = (username, code) => new Promise((resolve, reject) => {
  const user = new CognitoUser({
    Username: username,
    Pool: userPool
  })

  user.confirmRegistration(code, true, (error, result) => {
    if (error) {
      reject(error)
      return
    }
    resolve(result)
  })
})

const resendSignUpCode = username => new Promise((resolve, reject) => {
  const user = new CognitoUser({
    Username: username,
    Pool: userPool
  })

  user.resendConfirmationCode((error, result) => {
    if (error) {
      reject(error)
      return
    }
    resolve(result)
  })
})

const requestPasswordReset = username => new Promise((resolve, reject) => {
  const user = new CognitoUser({
    Username: username,
    Pool: userPool
  })

  user.forgotPassword({
    onSuccess () {
      resolve()
    },
    onFailure (error) {
      reject(error)
    },
    inputVerificationCode () {
      resolve()
    }
  })
})

const confirmPasswordReset = (username, code, newPassword) => new Promise((resolve, reject) => {
  const user = new CognitoUser({
    Username: username,
    Pool: userPool
  })

  user.confirmPassword(code, newPassword, {
    onSuccess () {
      resolve()
    },
    onFailure (error) {
      reject(error)
    }
  })
})

const signOut = () => {
  challengedUser = null
  const user = userPool.getCurrentUser()
  if (user) {
    user.signOut()
  }
}

const getAccessToken = async () => {
  const principal = await getCurrentPrincipal()
  if (!principal) {
    throw new Error('No authenticated Cognito session.')
  }
  return principal.accessToken
}

const getIdToken = async () => {
  const principal = await getCurrentPrincipal()
  if (!principal) {
    throw new Error('No authenticated Cognito session.')
  }
  return principal.idToken
}

const getGroups = async () => {
  const principal = await getCurrentPrincipal()
  return principal ? principal.groups : []
}

export {
  completeNewPassword,
  confirmPasswordReset,
  confirmSignUp,
  getAccessToken,
  getCurrentPrincipal,
  getGroups,
  getIdToken,
  requestPasswordReset,
  resendSignUpCode,
  signIn,
  signUp,
  signOut
}
